import React, { useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";

const BulkStudentUpload = () => {
  const [excelData, setExcelData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [loading, setLoading] = useState(false);

  const [alreadyUploaded, setAlreadyUploaded] = useState([]);
  const [result, setResult] = useState(null);

  /* ---------- FILE READ ---------- */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheet], {
        defval: "",
      });
      setExcelData(rows);
      setAlreadyUploaded([]);
      setResult(null);
    };
    reader.readAsBinaryString(file);
  };

  /* ---------- EDIT ---------- */
  const handleCellChange = (value, rowIndex, field) => {
    const updated = [...excelData];
    updated[rowIndex][field] = value;
    setExcelData(updated);
  };

  const deleteRow = (index) => {
    setExcelData(excelData.filter((_, i) => i !== index));
  };

  const toggleEdit = (index) => {
    setEditingRow(editingRow === index ? null : index);
  };

  /* ---------- SUBMIT ---------- */
  const handleSubmit = async () => {
    if (excelData.length === 0) {
      return alert("No data to upload");
    }

    setLoading(true);

    try {
      const sessionId = localStorage.getItem("currentSessionId");

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/students/bulkUpload`,
        { rows: excelData, sessionId }
      );

      setResult(res.data);
      setAlreadyUploaded(res.data.alreadyUploaded || []);

    } catch (err) {
      alert(err.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 bg-white min-h-screen">
      <div className="max-w-6xl mx-auto shadow-xl rounded-xl p-6 border">

        <h2 className="text-2xl font-bold text-blue-600 mb-6">
          Bulk Student Upload
        </h2>

        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          className="mb-4"
        />

        {/* ---------- PREVIEW TABLE ---------- */}
        {excelData.length > 0 && (
          <>
            <div className="overflow-x-auto max-h-[400px] border rounded">
              <table className="min-w-max w-full text-sm">
                <thead className="bg-blue-500 text-white sticky top-0">
                  <tr>
                    {Object.keys(excelData[0]).map((col) => (
                      <th key={col} className="px-4 py-2">{col}</th>
                    ))}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {excelData.map((row, i) => (
                    <tr key={i} className="border-b">
                      {Object.keys(row).map((col) => (
                        <td key={col} className="px-3 py-2">
                          {editingRow === i ? (
                            <input
                              value={row[col]}
                              onChange={(e) =>
                                handleCellChange(e.target.value, i, col)
                              }
                              className="border px-2 py-1 w-full"
                            />
                          ) : (
                            row[col]
                          )}
                        </td>
                      ))}
                      <td className="px-2">
                        <button onClick={() => toggleEdit(i)} className="text-blue-600">
                          {editingRow === i ? "Save" : "Edit"}
                        </button>
                        <button
                          onClick={() => deleteRow(i)}
                          className="text-red-600 ml-3"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="mt-5 px-6 py-3 bg-blue-600 text-white rounded"
            >
              {loading ? "Uploading..." : "Upload"}
            </button>
          </>
        )}

        {/* ---------- RESULT ---------- */}
        {result && (
          <div className="mt-6 p-4 bg-green-50 border rounded">
            <p className="font-semibold text-green-700">
              ✅ Uploaded: {result.insertedCount}
            </p>
            <p className="font-semibold text-yellow-700">
              ⚠ Already Uploaded: {result.alreadyUploadedCount}
            </p>
          </div>
        )}

        {/* ---------- DUPLICATE TABLE ---------- */}
        {alreadyUploaded.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              Already Uploaded Students
            </h3>

            <table className="w-full border text-sm">
              <thead className="bg-red-100">
                <tr>
                  <th className="border px-3 py-2">Username</th>
                  <th className="border px-3 py-2">Name</th>
                </tr>
              </thead>
              <tbody>
                {alreadyUploaded.map((s, i) => (
                  <tr key={i}>
                    <td className="border px-3 py-2">{s.username}</td>
                    <td className="border px-3 py-2">{s.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default BulkStudentUpload;
